/**
 * Componente de footer de paginação reutilizável
 * 
 * Exibe contador de itens, seletor de itens por página e navegação
 * Pode ser usado em tabelas, grids de cards e outras listagens
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

/**
 * Interface de props para o componente PaginationFooter
 * Configurações flexíveis para diferentes contextos de uso
 */
interface PaginationFooterProps {
  /** Total de itens (usado para calcular páginas e exibir contador) */
  totalItems: number;
  /** Página atual (1-indexed) */
  currentPage: number;
  /** Quantidade de itens por página */
  pageSize: number;
  /** Callback quando página muda */
  onPageChange: (page: number) => void;
  /** Callback quando tamanho da página muda */
  onPageSizeChange: (size: number) => void;
  /** Opções disponíveis para itens por página */
  pageSizeOptions?: number[];
  /** Classe CSS adicional para o container */
  className?: string;
  /** Classe CSS para a borda superior */
  borderClassName?: string;
}

/**
 * Componente de footer de paginação reutilizável
 * Exibe contador de itens, seletor de itens por página e navegação
 */
export function PaginationFooter({
  totalItems,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 15, 20],
  className,
  borderClassName = 'border-t',
}: PaginationFooterProps) {
  // Cálculos de paginação
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  /**
   * Gera array de números de página com ellipsis para navegação
   * Mostra até 5 páginas com ellipsis para conjuntos maiores
   */
  const getPageNumbers = (): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('ellipsis');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('ellipsis');
      pages.push(totalPages);
    }
    return pages;
  };

  /**
   * Handler para mudança de itens por página
   * Reseta para página 1 ao mudar tamanho
   */
  const handlePageSizeChange = (value: string) => {
    onPageSizeChange(Number(value));
    onPageChange(1);
  };

  // Não renderiza se não houver itens
  if (totalItems === 0) return null;

  return (
    <div className={cn(
      "flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 pt-4",
      borderClassName,
      className
    )}>
      {/* Contador e seletor de itens por página */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span>Exibindo {startIndex + 1}-{endIndex} de {totalItems}</span>
        <div className="flex items-center gap-2">
          <span>Por pág:</span>
          <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="w-16 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Botões de navegação */}
      <div className="flex items-center gap-1">
        <Button 
          variant="outline" 
          size="icon" 
          className="h-8 w-8" 
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        
        {getPageNumbers().map((page, i) => (
          page === 'ellipsis' ? (
            <span key={`ellipsis-${i}`} className="px-2 text-muted-foreground">...</span>
          ) : (
            <Button 
              key={page} 
              variant={currentPage === page ? "default" : "outline"}
              size="icon" 
              className="h-8 w-8"
              onClick={() => onPageChange(page)}
            >
              {page}
            </Button>
          )
        ))}
        
        <Button 
          variant="outline" 
          size="icon" 
          className="h-8 w-8"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
